import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Comment, WebsocketConversationMetaData } from 'api/conversations';
import { ProjectId } from 'api/models';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { RootState } from 'data';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useConversationReadReceiptSocketUpdate } from 'queries/project-global-conversation/read-receipt-ws';
import { getConversationAction } from './actions';
import { createComment, deleteComment, updateComment } from './slices';
import { ConversationContext, ConversationStore } from './types';

export const useConversationStore = (
  [, projectId]: ProjectId,
  context: ConversationContext,
): AtLeast<ValueOf<ConversationStore>, 'loading'> => {
  const dispatch = useDispatch();
  const conversation = useSelector(
    (state: RootState) => state.conversation[`${context.type}-${context.id}`],
  );

  useConversationReadReceiptSocketUpdate(projectId);
  useEffect(() => {
    dispatch(
      getConversationAction({
        projectId,
        context: {
          id: context.id,
          type: context.type,
        },
      }),
    );
  }, [projectId, context.id, context.type, dispatch]);

  const handleSocketUpdates = useCallback(
    (socketData: SocketData<Comment & WebsocketConversationMetaData>) => {
      const { data } = socketData;

      switch (socketData.action) {
        case Action.Create:
          dispatch(
            createComment({
              context: data.commentContext,
              comment: data,
            }),
          );
          break;

        case Action.Update:
          dispatch(
            updateComment({
              context: data.commentContext,
              comment: data,
            }),
          );
          break;

        case Action.Delete:
          dispatch(
            deleteComment({
              context: data.commentContext,
              comment: data,
            }),
          );
          break;

        default:
      }
    },
    [dispatch],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Create, ObjectType.ConversationComment),
  );
  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Update, ObjectType.ConversationComment),
  );
  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Delete, ObjectType.ConversationComment),
  );

  return { ...{ comments: [], loading: true }, ...conversation };
};
